// components/sections/contact/ContactForm.tsx
"use client";

import { useId } from "react";
import clsx from "clsx";
import { Loader2, Check, AlertCircle, RefreshCw } from "lucide-react";
import { useContactForm, type ContactFormValues } from "@/hooks/useContactForm";
import { CONTACT_REASONS } from "@/data/contact-page";

/* ─── Sub-components ──────────────────────────────────────────────── */

interface FieldShellProps {
  label: string;
  htmlFor: string;
  error?: string;
  required?: boolean;
  children: React.ReactNode;
}

function FieldShell({ label, htmlFor, error, required, children }: FieldShellProps) {
  return (
    <div className="flex flex-col gap-1.5">
      <label
        htmlFor={htmlFor}
        className="text-[11px] font-semibold uppercase tracking-[2.5px] text-stone-500"
      >
        {label}
        {required && <span className="ml-0.5 text-amber-600">*</span>}
      </label>
      {children}
      <div
        className={clsx(
          "flex items-center gap-1 text-[12px] transition-all duration-200",
          error ? "opacity-100 text-red-500" : "opacity-0 text-red-500 select-none"
        )}
        role={error ? "alert" : undefined}
        aria-live="polite"
      >
        {error && <AlertCircle size={11} strokeWidth={2.5} className="shrink-0" />}
        {error ?? "\u00A0"}
      </div>
    </div>
  );
}

const baseInput = "w-full rounded-xl border bg-white px-4 py-3 text-[15px] text-stone-800 placeholder:text-stone-300 focus:outline-none focus:ring-2 transition-all duration-200 shadow-sm";

const inputClass = (hasError: boolean) =>
  clsx(
    baseInput,
    hasError
      ? "border-red-300 focus:border-red-400 focus:ring-red-100"
      : "border-stone-200 focus:border-amber-400 focus:ring-amber-100"
  );

/* ─── Success state ───────────────────────────────────────────────── */

function SuccessState({ onReset }: { onReset: () => void }) {
  return (
    <div
      className="flex flex-col items-center justify-center text-center gap-5 py-16 px-8"
      role="status"
    >
      <div className="relative">
        <div className="flex h-16 w-16 items-center justify-center rounded-full border-2 border-amber-300 bg-amber-50 text-amber-600">
          <Check size={24} strokeWidth={2.5} />
        </div>
        <div className="absolute inset-0 rounded-full bg-amber-200/30 animate-ping" />
      </div>
      <div>
        <p className="text-[20px] font-bold text-stone-900">Message sent!</p>
        <p className="mt-2 text-[14px] text-stone-500 max-w-[280px] mx-auto">
          Thanks for reaching out. We typically reply within one working day.
        </p>
      </div>
      <button
        type="button"
        onClick={onReset}
        className="inline-flex items-center gap-2 rounded-full border border-stone-200 bg-white px-5 py-2.5 text-[13px] font-medium text-stone-600 hover:border-stone-300 hover:bg-stone-50 transition-all duration-200"
      >
        <RefreshCw size={13} />
        Send another message
      </button>
    </div>
  );
}

/* ─── Main form ───────────────────────────────────────────────────── */

interface ContactFormProps {
  onSubmit?: (values: ContactFormValues) => Promise<void>;
}

export function ContactForm({ onSubmit }: ContactFormProps) {
  const uid = useId();
  const id = (s: string) => `${uid}-${s}`;

  const {
    values,
    errors,
    status,
    setField,
    setTouchedField,
    handleSubmit,
    reset,
  } = useContactForm();

  if (status === "success") {
    return <SuccessState onReset={reset} />;
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-5">
      {/* Name + Email row */}
      <div className="grid gap-5 sm:grid-cols-2">
        <FieldShell
          label="Full name"
          htmlFor={id("name")}
          error={errors.name}
          required
        >
          <input
            id={id("name")}
            name="name"
            type="text"
            autoComplete="name"
            placeholder="Jane Smith"
            value={values.name}
            onChange={(e) => setField("name", e.target.value)}
            onBlur={() => setTouchedField("name")}
            className={inputClass(Boolean(errors.name))}
            aria-invalid={Boolean(errors.name)}
            aria-describedby={errors.name ? `${id("name")}-error` : undefined}
          />
        </FieldShell>

        <FieldShell
          label="Email address"
          htmlFor={id("email")}
          error={errors.email}
          required
        >
          <input
            id={id("email")}
            name="email"
            type="email"
            autoComplete="email"
            placeholder="you@example.com"
            value={values.email}
            onChange={(e) => setField("email", e.target.value)}
            onBlur={() => setTouchedField("email")}
            className={inputClass(Boolean(errors.email))}
            aria-invalid={Boolean(errors.email)}
          />
        </FieldShell>
      </div>

      {/* Reason */}
      <FieldShell label="Reason for contact" htmlFor={id("reason")}>
        <div className="flex flex-wrap gap-2" role="group" aria-label="Reason for contact">
          {CONTACT_REASONS.map((r) => (
            <button
              key={r.value}
              type="button"
              onClick={() => setField("reason", r.value)}
              aria-pressed={values.reason === r.value}
              className={clsx(
                "rounded-full border px-4 py-2 text-[12px] font-semibold uppercase tracking-[1.5px] transition-all duration-200",
                values.reason === r.value
                  ? "border-amber-400 bg-amber-100 text-amber-800 shadow-sm"
                  : "border-stone-200 bg-white text-stone-500 hover:border-stone-300 hover:bg-stone-50 hover:text-stone-700"
              )}
            >
              {r.label}
            </button>
          ))}
        </div>
      </FieldShell>

      {/* Message */}
      <FieldShell
        label="Message"
        htmlFor={id("message")}
        error={errors.message}
        required
      >
        <div className="relative">
          <textarea
            id={id("message")}
            name="message"
            rows={5}
            placeholder="Tell us what you're looking for — the more detail, the better."
            value={values.message}
            onChange={(e) => setField("message", e.target.value)}
            onBlur={() => setTouchedField("message")}
            className={clsx(inputClass(Boolean(errors.message)), "resize-none")}
            aria-invalid={Boolean(errors.message)}
          />
          <span
            className={clsx(
              "absolute bottom-3 right-3 text-[11px] tabular-nums transition-colors duration-200",
              values.message.length > 500
                ? "text-red-400"
                : values.message.length > 400
                  ? "text-amber-500"
                  : "text-stone-300"
            )}
          >
            {values.message.length}/500
          </span>
        </div>
      </FieldShell>

      {/* Submit */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4 pt-1">
        <button
          type="submit"
          disabled={status === "submitting"}
          className={clsx(
            "inline-flex items-center justify-center gap-2 rounded-full px-8 py-3.5",
            "text-[13px] font-bold uppercase tracking-[2.5px] transition-all duration-200",
            "bg-amber-700 text-white shadow-lg shadow-amber-200",
            "hover:bg-amber-800 hover:shadow-amber-300 hover:-translate-y-0.5",
            "active:translate-y-0 active:shadow-amber-200",
            "disabled:opacity-60 disabled:cursor-not-allowed disabled:translate-y-0 disabled:shadow-none",
            "self-start"
          )}
        >
          {status === "submitting" ? (
            <>
              <Loader2 size={15} className="animate-spin" />
              Sending…
            </>
          ) : (
            "Send message"
          )}
        </button>

        <p className="text-[12px] text-stone-400">
          We reply within one working day.
        </p>
      </div>

      {status === "error" && (
        <div
          className="flex items-start gap-2.5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-700"
          role="alert"
        >
          <AlertCircle size={15} className="shrink-0 mt-0.5" />
          <span>
            Something went wrong. Please try again, or email us directly at{" "}
            <a
              href="mailto:hello@queenscraft.com"
              className="underline underline-offset-2 hover:text-red-800"
            >
              hello@queenscraft.com
            </a>
          </span>
        </div>
      )}
    </form>
  );
}